import type { FocusEvent } from "react";
import { DimensionsInput } from "@freightsimple/ui-core/Controls/DimensionsInput";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";

import { getPluralFormForHandlingUnitType } from "@/Data/getPluralFormForHandlingUnitType";
import { getSingularFormForHandlingUnitType } from "@/Data/getSingularFormForHandlingUnitType";
import type { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import type { Units } from "@freightsimple/ui-core/Data/Units";
import { describeUnit, Dimension } from "@freightsimple/ui-core/Data/Units";

interface LineItemDimensionsInputProps {
  length: number | undefined;
  width: number | undefined;
  height: number | undefined;
  weight: number | undefined;
  numberHandlingUnits: number | undefined;
  handlingUnitType: HandlingUnitType;

  onLengthChange: (value: number | undefined) => void;
  onWidthChange: (value: number | undefined) => void;
  onHeightChange: (value: number | undefined) => void;
  onWeightChange: (value: number | undefined) => void;

  lengthErrorMessage: ErrorMessageType;
  widthErrorMessage: ErrorMessageType;
  heightErrorMessage: ErrorMessageType;
  weightErrorMessage: ErrorMessageType;

  maxSizeWarning?: number | undefined;
  minSizeWarning?: number | undefined;
  weightWarning?: ErrorMessageType;

  forceValidation: boolean;

  onBlur?: ((e: FocusEvent<HTMLInputElement>) => void) | undefined;

  units: Units;
}

// This is the app-level assembly: it computes the freight-specific strings
// (the weight label and the running-total line) and hands them to the shared
// ui-core `DimensionsInput`, which owns the layout.
const LineItemsDimensionsInput: React.FC<LineItemDimensionsInputProps> = (
  props: LineItemDimensionsInputProps,
) => {
  const { handlingUnitType, numberHandlingUnits } = props;

  const totalWeight =
    numberHandlingUnits === undefined || props.weight === undefined
      ? undefined
      : numberHandlingUnits * props.weight;

  const weightLabel = `Weight Per ${getSingularFormForHandlingUnitType(
    handlingUnitType,
  )} (${describeUnit(props.units, Dimension.Weight)})`;

  const summary =
    props.weightErrorMessage === undefined && totalWeight !== undefined
      ? `Each pallet is ${props.weight?.toLocaleString()}lb. Total Weight for ${numberHandlingUnits} ${getPluralFormForHandlingUnitType(
          handlingUnitType,
        )}: ${totalWeight.toLocaleString()}lb`
      : undefined;

  return (
    <DimensionsInput
      length={props.length}
      width={props.width}
      height={props.height}
      onLengthChange={props.onLengthChange}
      onWidthChange={props.onWidthChange}
      onHeightChange={props.onHeightChange}
      lengthErrorMessage={props.lengthErrorMessage}
      widthErrorMessage={props.widthErrorMessage}
      heightErrorMessage={props.heightErrorMessage}
      maxSizeWarning={props.maxSizeWarning}
      minSizeWarning={props.minSizeWarning}
      weightWarning={props.weightWarning}
      forceValidation={props.forceValidation}
      units={props.units}
      onBlur={props.onBlur}
      testIdPrefix="contents"
      weight={props.weight}
      onWeightChange={props.onWeightChange}
      weightErrorMessage={props.weightErrorMessage}
      weightLabel={weightLabel}
      summary={summary}
    />
  );
};
export default LineItemsDimensionsInput;
